import type { Service } from "./services";
import type { Location } from "./locations";

export type ContentFramework = "A" | "B" | "C" | "D" | "E";

export interface LocationServiceContent {
  framework: ContentFramework;
  heroHeadline: string;
  heroSubheadline: string;
  introSection: string;
  marketAnalysis: string;
  localBenefits: string[];
  processSteps: string[];
  faqs: { q: string; a: string }[];
  ctaText: string;
}

export function getFrameworkForLocation(location: Location): ContentFramework {
  if (location.countrySlug === "us") return "A";
  if (location.countrySlug === "europe") return "B";
  if (location.countrySlug === "uae") return "C";
  if (location.countrySlug === "india") return "D";
  if (location.countrySlug === "uk") return "E";
  return "A";
}

export function getFrameworkCopy(service: Service, location: Location): LocationServiceContent {
  const framework = getFrameworkForLocation(location);
  const commonBenefits = [
    `Localized delivery strategy for ${location.city}`,
    `Timezone-overlap collaboration for ${location.country} teams`,
    "Transparent milestones and weekly reporting",
    "Performance-focused implementation",
  ];

  if (framework === "A") {
    return {
      framework,
      heroHeadline: `${service.name} Built for ${location.city} Businesses`,
      heroSubheadline: `In ${location.city}, speed and measurable ROI matter. OWL delivers ${service.name.toLowerCase()} with enterprise-grade quality from our Delhi HQ for global execution.`,
      introSection: `${location.city} businesses operate in a high-competition environment where execution speed, reliability, and conversion outcomes directly impact growth. OWL helps local teams deploy ${service.name.toLowerCase()} that aligns with real business goals, not vanity metrics.`,
      marketAnalysis: `${location.marketContext} Our approach emphasizes reduced cycle time, rapid iteration, and outcome tracking for ${location.keyIndustries.join(", ")}.`,
      localBenefits: commonBenefits,
      processSteps: [
        "Discovery with regional market alignment",
        "Solution architecture and KPI planning",
        "Design and implementation sprints",
        "Testing and quality assurance",
        "Launch and optimization",
      ],
      faqs: [],
      ctaText: `Discuss Your ${location.city} Project on WhatsApp`,
    };
  }

  if (framework === "B") {
    return {
      framework,
      heroHeadline: `${service.name} for Growth Across ${location.city}`,
      heroSubheadline: `${location.city} businesses need compliance-aware digital systems. OWL delivers ${service.name.toLowerCase()} with GDPR-minded implementation and long-term maintainability.`,
      introSection: `${location.city} companies are scaling in tightly regulated and quality-sensitive markets. OWL builds ${service.name.toLowerCase()} that supports growth while preserving operational control and governance.`,
      marketAnalysis: `${location.marketContext} We tailor delivery for ${location.keyIndustries.join(", ")} with privacy, security, and reporting standards built in.`,
      localBenefits: [...commonBenefits, "GDPR-aware architecture and implementation guidance"],
      processSteps: [
        "Local context discovery and compliance mapping",
        "Architecture aligned to regional requirements",
        "Implementation with governance checkpoints",
        "Security and performance validation",
        "Launch with post-go-live support",
      ],
      faqs: [],
      ctaText: `Start Your ${location.city} Consultation`,
    };
  }

  if (framework === "C") {
    return {
      framework,
      heroHeadline: `${service.name} for Ambitious ${location.city} Teams`,
      heroSubheadline: `${location.city} is moving fast on digital transformation. OWL helps businesses launch premium, scalable ${service.name.toLowerCase()} with rapid execution.`,
      introSection: `${location.city} organizations are under pressure to innovate quickly while maintaining premium customer experiences. OWL delivers ${service.name.toLowerCase()} that combines speed, reliability, and polished execution.`,
      marketAnalysis: `${location.marketContext} We focus on multilingual-ready experiences, robust cloud delivery, and high-availability workflows for ${location.keyIndustries.join(", ")}.`,
      localBenefits: [...commonBenefits, "Rapid launch with premium user experience quality"],
      processSteps: [
        "Business objective and market-readiness workshop",
        "Solution blueprint and deployment plan",
        "Rapid implementation cycle",
        "QA with performance and reliability focus",
        "Launch with support and scaling roadmap",
      ],
      faqs: [],
      ctaText: `Discuss ${service.name} for ${location.city}`,
    };
  }

  if (framework === "D") {
    return {
      framework,
      heroHeadline: `${service.name} That Helps ${location.city} Teams Scale`,
      heroSubheadline: `${location.city}'s digital ecosystem rewards speed and efficiency. OWL provides high-quality ${service.name.toLowerCase()} with practical commercial value.`,
      introSection: `${location.city} businesses are scaling quickly and need systems that are stable, efficient, and outcome-focused. OWL delivers ${service.name.toLowerCase()} from Delhi with strong communication and measurable execution.`,
      marketAnalysis: `${location.marketContext} We support ${location.keyIndustries.join(", ")} with implementation that balances cost efficiency and engineering depth.`,
      localBenefits: [...commonBenefits, "Strong value-to-cost ratio with enterprise quality"],
      processSteps: [
        "Business requirement discovery",
        "Roadmap and milestone planning",
        "Build and iterative review",
        "Testing and hardening",
        "Launch with support handover",
      ],
      faqs: [],
      ctaText: `Get a ${location.city} Project Estimate`,
    };
  }

  return {
    framework,
    heroHeadline: `${service.name} for Enterprise Teams in ${location.city}`,
    heroSubheadline: `${location.city} organizations expect reliability, governance, and long-term delivery confidence. OWL delivers ${service.name.toLowerCase()} with that standard.`,
    introSection: `Enterprise and scale-up teams in ${location.city} need predictable implementation and transparent partner communication. OWL provides both through structured delivery and measurable outcomes.`,
    marketAnalysis: `${location.marketContext} Our teams align with business and compliance expectations in ${location.country}.`,
    localBenefits: [...commonBenefits, "Long-term partnership and governance-driven delivery"],
    processSteps: [
      "Stakeholder and requirement alignment",
      "Architecture and governance planning",
      "Implementation in controlled phases",
      "Security, QA, and acceptance testing",
      "Launch and continuous improvement",
    ],
    faqs: [],
    ctaText: `Plan Your ${location.city} Rollout`,
  };
}
